import { Link, useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useCareerOpening } from '../hooks/useCareers';

const BRAND_YELLOW = '#E0A15E';

const Page = styled.main`
  min-height: 100vh;
  padding: calc(84px + env(safe-area-inset-top)) 1.1rem 2rem;
  background:
    radial-gradient(1000px 560px at 10% 4%, rgba(224, 161, 94, 0.3) 0%, rgba(224, 161, 94, 0) 62%),
    radial-gradient(980px 620px at 92% 14%, rgba(166, 76, 68, 0.25) 0%, rgba(166, 76, 68, 0) 60%),
    linear-gradient(180deg, #111625 0%, #090d19 100%);
  color: #f4f6fb;
`;

const Container = styled.div`
  width: min(980px, 100%);
  margin: 0 auto;
  display: grid;
  gap: 1rem;
`;

const Card = styled.section`
  border-radius: 18px;
  padding: clamp(1.25rem, 3vw, 2.2rem);
  display: grid;
  gap: 0.8rem;
`;

const Eyebrow = styled.span`
  display: inline-flex;
  width: fit-content;
  padding: 0.35rem 0.72rem;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.1);
  font-size: 0.74rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  font-weight: 800;
`;

const Title = styled.h1`
  margin: 0;
  font-size: clamp(1.8rem, 4vw, 3rem);
  line-height: 1.02;
  color: ${BRAND_YELLOW};
`;

const Role = styled.p`
  margin: 0;
  font-size: 0.94rem;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: rgba(244, 246, 251, 0.8);
  font-weight: 800;
`;

const Body = styled.p`
  margin: 0;
  font-size: 1rem;
  line-height: 1.6;
  color: rgba(244, 246, 251, 0.92);
  max-width: 70ch;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
`;

const PrimaryLink = styled(Link)`
  width: fit-content;
  text-decoration: none;
  border-radius: 999px;
  border: 1px solid rgba(224, 161, 94, 0.95);
  color: #111625;
  background: ${BRAND_YELLOW};
  padding: 0.5rem 0.92rem;
  font-size: 0.84rem;
  font-weight: 800;
`;

type ThankYouState = {
  jobTitle?: string;
  requisitionId?: string;
};

function titleFromSlug(rawSlug: string | undefined): string {
  const slug = (rawSlug ?? '').trim().toLowerCase();
  if (!slug) return 'this role';
  return slug
    .split('-')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export default function CareerApplicationThankYouPage() {
  const { jobSlug } = useParams<{ jobSlug: string }>();
  const location = useLocation();
  const state = (location.state ?? {}) as ThankYouState;
  const { job } = useCareerOpening(jobSlug);

  const jobTitle = state.jobTitle?.trim() || job?.title || titleFromSlug(jobSlug);
  const requisitionId = state.requisitionId?.trim() || job?.requisitionId || '';
  return (
    <Page>
      <Container>
        <Card>
          <Eyebrow>Application submitted</Eyebrow>
          <Title>Thank you for applying</Title>
          <Role>
            {jobTitle}
            {requisitionId ? ` • ${requisitionId}` : ''}
          </Role>
          <Body>
            Thank you for applying to the {jobTitle} role at Cybard. A member of our team will
            review your application shortly. If you are a good fit, we will reach out to you.
          </Body>
          <Actions>
            <PrimaryLink to="/careers">Explore other roles</PrimaryLink>
          </Actions>
        </Card>
      </Container>
    </Page>
  );
}
